import React, { useEffect, useState } from "react";
import { ClockIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Badge } from "../../../ui/Badge";
import { Button } from "../../../ui/Button";
import { CriteriaEditor } from "./CriteriaEditor";
import { DeleteEvaluatorDialog } from "./DeleteEvaluatorDialog";
import { VersionHistory } from "./VersionHistory";
import { useEvaluatorVersions } from "../hooks/useEvaluatorVersions";
import {
  composeCriteria,
  criteriaError,
  splitCriteria,
  type CriterionPrefix,
} from "../utils/criteria";
import type { Evaluator } from "../types";

interface EvaluatorDetailProps {
  evaluator: Evaluator;
  isSubmitting: boolean;
  onUpdate: (id: string, criteria: string) => void;
  onDelete: (id: string) => void;
  /** Reports unsaved input so the page can guard navigation away from it. */
  onDirtyChange?: (isDirty: boolean) => void;
}

export function EvaluatorDetail({
  evaluator,
  isSubmitting,
  onUpdate,
  onDelete,
  onDirtyChange,
}: EvaluatorDetailProps) {
  const initial = splitCriteria(evaluator.criteria);
  const [prefix, setPrefix] = useState<CriterionPrefix | null>(initial.prefix);
  const [body, setBody] = useState(initial.body);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // The active version's id changes on every save, so it doubles as the signal that the
  // version chain needs refetching.
  const { versions, isLoading, error } = useEvaluatorVersions(
    evaluator.group_id,
    evaluator.id
  );

  // Reset the draft whenever a different evaluator — or a newly saved version of this
  // one — becomes active.
  useEffect(() => {
    const next = splitCriteria(evaluator.criteria);
    setPrefix(next.prefix);
    setBody(next.body);
    setConfirmingDelete(false);
  }, [evaluator.id, evaluator.criteria]);

  const draft = composeCriteria(prefix, body);
  const isDirty = draft !== evaluator.criteria;
  const validationError = criteriaError(prefix, body);
  const canSave = isDirty && !validationError && !isSubmitting;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const handleDiscard = () => {
    const next = splitCriteria(evaluator.criteria);
    setPrefix(next.prefix);
    setBody(next.body);
  };

  const handleSave = () => {
    if (!canSave) return;
    onUpdate(evaluator.id, draft);
  };

  const handleCopyToEditor = (criteria: string) => {
    const next = splitCriteria(criteria);
    setPrefix(next.prefix);
    setBody(next.body);
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="max-w-3xl w-full px-6 py-6 flex flex-col gap-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-foreground font-mono break-words">
              {evaluator.group_id}
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Reference this name from the API. It cannot be changed.
            </p>
          </div>
          <Button
            variant="dangerOutline"
            size="sm"
            onClick={() => setConfirmingDelete(true)}
            disabled={isSubmitting}
          >
            <TrashIcon className="w-4 h-4" />
            Delete
          </Button>
        </div>

        <CriteriaEditor
          prefix={prefix}
          body={body}
          disabled={isSubmitting}
          error={isDirty ? validationError : null}
          onPrefixChange={setPrefix}
          onBodyChange={setBody}
        />

        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={!canSave}
            title={validationError ?? undefined}
          >
            {isSubmitting ? "Saving…" : "Save new version"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDiscard}
            disabled={!isDirty || isSubmitting}
          >
            Discard changes
          </Button>
          {isDirty && !validationError && (
            <span className="text-xs text-muted-foreground">
              Saving keeps the old wording in history.
            </span>
          )}
        </div>

        <div className="border-t border-border pt-5 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <ClockIcon className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-sm font-medium text-foreground">History</h3>
            {versions.length > 1 && (
              <Badge variant="default">{versions.length} versions</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Editing a criterion creates a new version and retires the old one. Scores
            recorded under an earlier version keep that version&apos;s wording.
          </p>
          <VersionHistory
            versions={versions}
            isLoading={isLoading}
            error={error}
            onCopyToEditor={handleCopyToEditor}
          />
        </div>
      </div>

      {confirmingDelete && (
        <DeleteEvaluatorDialog
          evaluator={evaluator}
          versionCount={isLoading ? null : versions.length}
          isSubmitting={isSubmitting}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() => onDelete(evaluator.id)}
        />
      )}
    </div>
  );
}
